import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

const YEAR_SUFFIX = /-\d{4}$/;

function splitTags(value) {
  return (value || '')
    .split('||')
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function getModelKey(tag) {
  if (!tag) return null;

  const cleanTag = decodeURIComponent(tag).trim();

  if (YEAR_SUFFIX.test(cleanTag)) {
    return cleanTag.replace(YEAR_SUFFIX, '');
  }

  return cleanTag;
}

function escapeHtml(value = '') {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function formatMoney(value) {
  const amount = Number(value);

  if (Number.isNaN(amount)) return '';

  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(amount);
}

function addSportsbikeRecommendationStyles() {
  if (document.getElementById('sb-model-recommendation-styles')) return;

  const style = document.createElement('style');
  style.id = 'sb-model-recommendation-styles';

  style.textContent = `
    .sb-model-rec-item {
      min-width: 0;
    }

    .sb-model-rec-link {
      display: block;
      color: inherit;
      text-decoration: none;
      height: 100%;
    }

    .sb-model-rec-image {
      width: 100%;
      aspect-ratio: 1 / 1;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      margin-bottom: 14px;
    }

    .sb-model-rec-image img {
      width: 100%;
      height: 100%;
      display: block;
      object-fit: contain;
    }

    .sb-model-rec-title {
      font-size: 1rem;
      line-height: 1.3;
      margin: 0 0 7px;
    }

    .sb-model-rec-price {
      font-size: 1rem;
      line-height: 1.2;
      font-weight: 700;
      margin: 0;
    }

    .sb-model-rec-link:hover .sb-model-rec-title {
      color: #cc0000;
    }

    @media screen and (max-width: 749px) {
      .sb-model-rec-image {
        margin-bottom: 10px;
      }

      .sb-model-rec-title,
      .sb-model-rec-price {
        font-size: 0.92rem;
      }
    }
  `;

  document.head.appendChild(style);
}

class ProductRecommendations extends Component {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();

      if (this.#hasSportsbikeFitmentData()) {
        this.#loadSameModelProducts();
      } else {
        this.#loadShopifyRecommendations();
      }
    },
    {
      rootMargin: '0px 0px 400px 0px',
    }
  );

  #cachedRecommendations = {};
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();

    addSportsbikeRecommendationStyles();

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  #hasSportsbikeFitmentData() {
    return Boolean(this.dataset.currentProductTags);
  }

  #getSelectedModelKey() {
    const currentTags = splitTags(
      this.dataset.currentProductTags
    );

    /*
     * FIRST CHOICE:
     * Part Finder vehicle stored in session.
     */
    let selectedVehicle =
      sessionStorage.getItem('sportsbike_vehicle');

    /*
     * SECOND CHOICE:
     * GoVedia filter tag in the URL.
     */
    const params = new URLSearchParams(
      window.location.search
    );

    const urlVehicle =
      params.get('filter.p.tag');

    if (urlVehicle) {
      selectedVehicle = urlVehicle;
    }

    /*
     * Only trust the selected bike if the current
     * product actually fits that model family.
     */
    if (selectedVehicle) {
      const selectedModel =
        getModelKey(selectedVehicle);

      const productFitsSelectedModel =
        currentTags.some((tag) => {
          return getModelKey(tag) === selectedModel;
        });

      if (productFitsSelectedModel) {
        return selectedModel;
      }
    }

    /*
     * FALLBACK:
     * Find a Year/Make/Model fitment tag on the
     * current product and use its model family.
     */
    const yearTag = currentTags.find((tag) =>
      YEAR_SUFFIX.test(tag)
    );

    if (yearTag) {
      return getModelKey(yearTag);
    }

    return null;
  }

  async #loadSameModelProducts() {
    const modelKey = this.#getSelectedModelKey();

    if (!modelKey) {
      this.#loadShopifyRecommendations();
      return;
    }

    const maxProducts = Number.parseInt(
      this.dataset.maxProducts || '4',
      10
    );

    const currentProductId =
      String(this.dataset.productId || '');

    /*
     * Search Shopify specifically through PRODUCT TAGS.
     *
     * Example:
     * Ducati-Panigale-V4-2023
     * Ducati-Panigale-V4-2024
     * Ducati-Panigale-V4-2025
     *
     * all reduce to:
     * Ducati-Panigale-V4
     */
    const searchUrl =
      `${window.Shopify.routes.root}search/suggest.json` +
      `?q=${encodeURIComponent(modelKey)}` +
      `&resources[type]=product` +
      `&resources[limit]=10` +
      `&resources[limit_scope]=each` +
      `&resources[options][fields]=tag` +
      `&resources[options][unavailable_products]=show`;

    try {
      this.#activeFetch?.abort();

      this.#activeFetch =
        new AbortController();

      const response = await fetch(searchUrl, {
        signal: this.#activeFetch.signal,
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(
          `Model search returned ${response.status}`
        );
      }

      const data = await response.json();

      const searchProducts =
        data?.resources?.results?.products || [];

      /*
       * Predictive search can loosely match terms.
       * This second filter makes it strict:
       *
       * the candidate must genuinely have a
       * Year/Make/Model tag for this exact model.
       */
      const sameModelProducts =
        searchProducts
          .filter((candidate) => {
            if (
              String(candidate.id) === currentProductId
            ) {
              return false;
            }

            const candidateTags =
              candidate.tags || [];

            return candidateTags.some((tag) => {
              return getModelKey(tag) === modelKey;
            });
          })
          .slice(0, maxProducts);

      if (!sameModelProducts.length) {
        this.#hideRecommendations();
        return;
      }

      this.#renderSameModelProducts(
        sameModelProducts
      );
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error(
        'Sportsbike same-model recommendations:',
        error
      );

      this.#hideRecommendations();
    } finally {
      this.#activeFetch = null;
    }
  }

  #renderSameModelProducts(products) {
    const lists = [
      ...this.querySelectorAll(
        '.resource-list'
      ),
    ];

    if (!lists.length) return;

    const html = products
      .map((product) => {
        const image =
          product.featured_image?.url ||
          product.image ||
          '';

        const imageAlt =
          product.featured_image?.alt ||
          product.title ||
          '';

        return `
          <div
            class="resource-list__item sb-model-rec-item"
            data-product-id="${escapeHtml(product.id)}"
          >
            <a
              class="sb-model-rec-link"
              href="${escapeHtml(product.url)}"
            >
              <div class="sb-model-rec-image">
                ${
                  image
                    ? `
                      <img
                        src="${escapeHtml(image)}"
                        alt="${escapeHtml(imageAlt)}"
                        loading="lazy"
                        width="600"
                        height="600"
                      >
                    `
                    : ''
                }
              </div>

              <div class="sb-model-rec-title">
                ${escapeHtml(product.title)}
              </div>

              <div class="sb-model-rec-price">
                ${formatMoney(product.price)}
              </div>
            </a>
          </div>
        `;
      })
      .join('');

    /*
     * Replace Shopify's unrelated recommendation
     * products with the same-model products.
     */
    lists.forEach((list) => {
      list.innerHTML = html;

      list.dataset.hasRecommendations =
        'true';
    });

    this.classList.remove('hidden');
    this.dataset.recommendationsPerformed =
      'true';
  }

  #hideRecommendations() {
    this.querySelectorAll(
      '[data-has-recommendations]'
    ).forEach((list) => {
      list.dataset.hasRecommendations =
        'false';
    });

    this.classList.add('hidden');
  }

  /*
   * Normal Shopify behaviour remains available
   * for any recommendation block that doesn't
   * have our Sportsbike fitment data.
   */
  #loadShopifyRecommendations() {
    const {
      productId,
      recommendationsPerformed,
      sectionId,
      intent,
    } = this.dataset;

    if (!productId || !sectionId) return;

    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(
      productId,
      sectionId,
      intent
    )
      .then((result) => {
        if (!result.success) {
          throw new Error(
            `Server returned ${result.status}`
          );
        }

        if (!result.data?.trim()) {
          throw new Error(
            'No recommendations available'
          );
        }

        this.dataset.recommendationsPerformed =
          'true';

        morphSection(
          sectionId,
          result.data,
          {
            mode: 'hydration',
            injectStylesheet: true,
          }
        );
      })
      .catch((error) => {
        console.error(
          'Product recommendations error:',
          error
        );

        this.#hideRecommendations();
      });
  }

  async #fetchCachedRecommendations(
    productId,
    sectionId,
    intent
  ) {
    const url =
      `${this.dataset.url}` +
      `&product_id=${productId}` +
      `&section_id=${sectionId}` +
      `&intent=${intent}`;

    if (this.#cachedRecommendations[url]) {
      return {
        success: true,
        data: this.#cachedRecommendations[url],
      };
    }

    const response = await fetch(url);

    if (!response.ok) {
      return {
        success: false,
        status: response.status,
      };
    }

    const text = await response.text();

    this.#cachedRecommendations[url] = text;

    return {
      success: true,
      data: text,
    };
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define(
    'product-recommendations',
    ProductRecommendations
  );
}